import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popup } from 'semantic-ui-react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { Button, FlexContainer, Loading } from 'common/components/atoms';
import Modal from 'common/components/organisms/Modal';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getMachineBuilderMetadata } from 'home/Machines/MachineBuilder/actions/machineBuilder.actions';
import { getUsers } from 'home/Settings/EventDefinitions/definition.actions';
import { getMachineConditionOptions } from 'common/constants';
import { roleOptions } from 'home/Settings/AccountUsers/accountUsers.constants';
import { humanize } from 'common/helpers';
import InputEditOnDemand from 'common/components/molecules/InputEditOnDemand';
import PlusSvg from 'common/images/PlusSvg';
import MsgContainer from 'common/components/atoms/MsgContainer';
import * as machineDetailsActions from 'home/Machines/MachineDetails/machineDetails.actions';
import * as failureModesActions from 'home/MachineDiagnostics/actions/failureModes.actions';
import { getSensorsList } from 'home/Sensors/actions/sensors.actions';
import InputField from 'common/components/atoms/InputField';
import fonts from 'common/styles/fonts';
import { getComponentsFromMachineId } from 'home/AssetHierarchy/utils/assetHierarchyUtils';
import { getComponentOptions } from 'home/Machines/MachineDetails/MachineCharts/actions/machineCharts.actions';
import RBAC from 'common/rbac/RBAC';
import { mapComponentToResource, operations } from 'common/rbac/constants';
import { ArrowDown, ArrowUp } from 'common/images/FaIcons';
import { toastr } from 'react-redux-toastr';
import { isPetasenseAdmin } from 'common/rbac/util';
import colors from 'common/styles/colors';
import * as templateActions from 'home/TaskTemplates/actions/template.actions';
import { formatCurrency, getCurrencyFromState } from 'common/utils/currencyUtils';
import { getFailureModeDefectLabel } from 'common/utils/failureModeDisplayName';
import { getComponentTypeDisplayName } from 'common/utils/componentTypeDisplayName';
import TaskTitleWithId from './TaskTitleWithId';
import ListContainer from './atoms/ListContainer';
import RepairDetails from './RepairDetails';
import CollapsibleBox from './atoms/CollapsibleBox';
import NoteDetails from './NoteDetails';
import Footer from './atoms/Footer';
import * as taskActions from '../actions/tasks.actions';
import * as repairActions from '../actions/repairs.actions';
import * as noteActions from '../actions/notes.actions';
import TaskHistory from './TaskHistory';
import RepairsTimeline from './RepairsTimeline';
import NotesTimeline from './NotesTimeline';
import Attachments from '../organisms/Attachments';
import TaskTriggers from './TaskTriggers';
import { getChartSearchParams } from '../utils';
import { isSelectableDefect } from './taskDefectField';
import {
  DropdownSelect,
  H3,
  Title,
  Description,
  MultiSelectDropdown,
  SelectFieldWrapper,
  SectionHeader,
  SectionBlock,
  RightPaneFields,
  ActivityTabRow,
  ActivityTab,
  TemplateCardsRow,
  TemplateCard,
  TemplateCardTitle,
  TemplateCardDescription,
  TemplateCardSteps,
  TemplateFilterRow,
  TemplateFilterPill,
  TemplateFilterExpandRow,
  TemplateFilterToggle,
  BackToDetailsButton,
  CostSavedHelper,
  DescriptionBoxWrapper,
  RecommendedActionBoxWrapper,
  TaskTitleWrapper,
  TaskTitleUnderline,
  SelectTemplateBanner,
  SelectTemplateTitle,
  SelectTemplateSubtitle,
  CostSavedInputWrapper,
  CostSavedOuterWrapper,
  CloseTaskInputWrapper,
  CloseTaskConfirmButton,
  fontSettings
} from './TaskDetailsModal.styled';

const FollowersSelectionView = ({ text, showFollowIcon, onFollowClick, followTaskTitle }) => (
  <FlexContainer justifyContent="space-between" width="100%">
    <span>{text}</span>
    {showFollowIcon && (
      <span title={followTaskTitle} onClick={onFollowClick}><PlusSvg /></span>
    )}
  </FlexContainer>
);

function TaskDetailsModal(props) {
  const { t, i18n } = useTranslation();
  const DEFECT_FILTER_COLLAPSE_COUNT = 3;

  const getDefectOptions = () => {
    let defectOptions = [{ value: 0, text: t('common.na') }];
    if (props.machineDetails.object && props.machineDetails.object.asset_type === 'non_rotating') {
      if (props.failureModes.list && props.machineDetails.object.sub_type) {
        const failureModes = props.failureModes.list.filter(fm => fm.sub_type === props.machineDetails.object.sub_type)
          .map(fm => ({ value: fm.id, text: getFailureModeDefectLabel(t, fm.id, fm.name) }));
        defectOptions = [...defectOptions, ...failureModes];
      }
    } else if (props.machineDetails.object && props.failureModes.list) {
      const components = getComponentsFromMachineId(props.hierarchy, props.machineDetails.object.id);
      const machineComponentTypes = components.map(c => c.node_sub_type);
      const rotatingFailureModes = props.failureModes.list.filter((fm) => {
        if (fm.asset_type !== 'rotating') return false;
        const failureModeComponentTypes = fm.component_types;
        if (_.isNil(failureModeComponentTypes) || _.isEmpty(failureModeComponentTypes)) return true;
        return _.some(machineComponentTypes, ct => failureModeComponentTypes.includes(ct));
      }).map(fm => ({ value: fm.id, text: getFailureModeDefectLabel(t, fm.id, fm.name) }));
      defectOptions = [...defectOptions, ...rotatingFailureModes];
    }

    return defectOptions;
  };

  const getComponentTypeLabel = value =>
    getComponentTypeDisplayName(t, i18n, { value: value || '' });

  const getComponentList = (components) => {
    const items = [];
    components.forEach((element) => {
      items.push({
        text: getComponentTypeLabel(element.component_subtype),
        value: element.component_subtype
      });
    });
    return items;
  };

  const getTaskStateLabel = (value = '') => (
    t(`taskDetailsModal.options.states.${value}`, { defaultValue: humanize(value) })
  );

  const getTaskTypeLabel = (value = '') => (
    t(`taskDetailsModal.options.taskTypes.${value}`, { defaultValue: humanize(value) })
  );

  const getRoleLabel = (value = '') => (
    t(`taskDetailsModal.options.roles.${value}`, { defaultValue: humanize(value) })
  );

  const getTaskStateOptions = (states = []) => (
    states.map(value => ({
      value,
      text: getTaskStateLabel(value)
    }))
  );

  const getTaskTypeOptions = (types = ['asset_maintenance', 'instrumentation']) => (
    types.map(value => ({
      value,
      text: getTaskTypeLabel(value)
    }))
  );

  const getRoleOptions = () => (
    roleOptions.map(option => ({
      ...option,
      text: getRoleLabel(option.value)
    }))
  );

  const onTaskSave = () => {
    const params = {
      name,
      state,
      description,
      task_type,
      user_role_assigned,
      machine_id: props.machineId || props.machine_id,
      devices_list,
      triggers_details: _.cloneDeep(selectedTriggers),
      component_type
    };
    if (params.triggers_details) {
      Object.keys(params.triggers_details).forEach((key) => {
        delete params.triggers_details[key].selected;
      });
    }
    if (!_.isEmpty(recommended_action)) {
      params.recommended_action = recommended_action;
    }
    if (task_type === 'asset_maintenance') {
      params.condition = condition;
      if (isSelectableDefect(defect)) params.defect_id = Number(defect);
    }
    if (props.taskDetails.ara_id) {
      params.ara_id = props.taskDetails.ara_id;
    }
    setShowSaveLoader(true);
    const isAssetsPage = props.location.pathname === '/assets';
    props.taskActions.addTask(params, isAssetsPage).then(
      () => {
        if (_.isEmpty(props.files)) {
          setShowSaveLoader(false);
          props.closeModal();
        }
      },
      () => {
        setShowSaveLoader(false);
      }
    );
  };

  const onTaskTemplateCreate = () => {
    if (!checkMandatoryFeildsTemplateCreation()) {
      return;
    }
    const params = {
      name,
      description,
      task_type,
      component_type,
      is_global: isPetasenseAdmin(props.user)
    };
    if (!_.isEmpty(recommended_action)) {
      params.recommended_action = recommended_action;
    }
    if (task_type === 'asset_maintenance') {
      params.condition = condition;
      if (isSelectableDefect(defect)) params.defect_id = Number(defect);
    }
    setShowSaveLoader(true);
    props.templateActions.createTemplate(params).then(
      () => {
        setShowSaveLoader(false);
        props.closeModal();
      },
      () => {
        setShowSaveLoader(false);
      }
    );
  };

  const checkMandatoryFeildsTemplateCreation = () => {
    if (_.isEmpty(name)) {
      toastr.error(t('taskDetailsModal.errors.enterTaskName'));
      return false;
    }
    if (_.isEmpty(recommended_action)) {
      toastr.error(t('taskDetailsModal.errors.enterRecommendedAction'));
      return false;
    }
    if (_.isEmpty(description)) {
      toastr.error(t('taskDetailsModal.errors.enterDescription'));
      return false;
    }
    if (_.isEmpty(task_type)) {
      toastr.error(t('taskDetailsModal.errors.selectTaskType'));
      return false;
    }
    if (task_type === 'asset_maintenance' && _.isEmpty(condition)) {
      toastr.error(t('taskDetailsModal.errors.selectCondition'));
      return false;
    }
    if (task_type === 'asset_maintenance') {
      const conditionOptions = getMachineConditionOptions(3, t);
      if (_.isEmpty(conditionOptions.find(opt => opt.value === condition))) {
        toastr.error(t('taskDetailsModal.errors.conditionTemplateRule'));
        return false;
      }
    }
    if (_.isEmpty(component_type)) {
      toastr.error(t('taskDetailsModal.errors.selectComponentType'));
      return false;
    }
    return true;
  };

  const onTaskUpdate = (field, value) => {
    if (!props.taskDetails.id) {
      return;
    }
    const params = {};
    params[field] = value;
    props.taskActions.updateTask(props.taskDetails.id, params);
  };

  const onTemplateSelection = (value) => {
    const selected_template = template_Data.find(element => (element.id === value));
    if (selected_template) {
      setName(selected_template.name);
      setTemplateName(selected_template.name);
      setRecommendedAction(selected_template.recommended_action);
      setDescription(selected_template.description);
      setTaskType(selected_template.task_type);
      setComponentType(selected_template.component_type);
      if (selected_template.task_type === 'asset_maintenance') {
        // Match by numeric defect_id first (stable); fall back to API failure-mode name (not translated label)
        let defectMatch = defectOptions.find(el => el.value === selected_template.defect_id);
        if (!defectMatch && selected_template.suspected_defect && props.failureModes.list) {
          const fm = props.failureModes.list.find(f => f.name === selected_template.suspected_defect);
          if (fm) defectMatch = defectOptions.find(el => el.value === fm.id);
        }
        if (defectMatch) setDefect(defectMatch.value);
        setCondition(selected_template.condition);
      }
    }
  };


  const onDropdownChange = (value, field) => {
    // If changing state to "closed" for an asset maintenance task, show the cost savings popup
    if (field === 'state' && value === 'closed' && props.isEditMode && task_type === 'asset_maintenance') {
      setPendingCloseState(value);
      setShowCloseSavingsPopup(true);
      return;
    }

    switch (field) {
      case 'state':
        setTaskState(value);
        break;
      case 'user_role_assigned':
        setAssignedTo(value);
        break;
      case 'component_type':
        setComponentType(value);
        break;
      case 'task_type':
        setTaskType(value);

        // If task type is changed to instrumentation, cost savings should not apply.
        // Delete/clear customer_savings in the database (same as the Delete button).
        if (value === 'instrumentation') {
          if (customer_savings !== '') {
            setCustomerSavings('');
          }

          if (props.isEditMode && props.taskDetails && props.taskDetails.id && props.taskDetails.customer_savings != null) {
            const params = { task_type: 'instrumentation' };
            params.customer_savings = null;
            props.taskActions.updateTask(
              props.taskDetails.id,
              params,
              t('taskDetailsModal.costSavingsRemoved', { defaultValue: 'Cost savings removed' })
            );

            // Prevent the generic onTaskUpdate(field, value) below from firing a second request.
            return;
          }
        }
        break;
      case 'condition':
        setCondition(value);
        break;
      case 'defect_id':
        setDefect(value);
        break;
      default:
        break;
    }
    if (props.isEditMode && props.taskDetails[field] !== value) {
      onTaskUpdate(field, value);
    }
  };

  const handleCloseSavingsConfirm = () => {
    // Update state to closed
    setTaskState(pendingCloseState);

    // Update task with both state and customer_savings
    const updateParams = { state: pendingCloseState };
    const hasSavingsValue = customer_savings && customer_savings.trim() !== '';
    const parsedSavings = hasSavingsValue ? parseFloat(customer_savings) : 0;
    if (hasSavingsValue && (Number.isNaN(parsedSavings) || parsedSavings < 0)) {
      toastr.error(t('taskDetailsModal.errors.invalidCostSavings'));
      return;
    }
    updateParams.customer_savings = parsedSavings;
    props.taskActions.updateTask(
      props.taskDetails.id,
      updateParams,
      t('taskDetailsModal.costSavingsUpdated', { defaultValue: 'Cost savings updated successfully' })
    );

    // Close popup and reset
    setShowCloseSavingsPopup(false);
    setPendingCloseState(null);
  };

  const handleCloseSavingsCancel = () => {
    setShowCloseSavingsPopup(false);
    setPendingCloseState(null);
    setCustomerSavings('');
  };

  const handleCostSavingsUpdate = () => {
    if (!props.taskDetails.id || !props.isEditMode) {
      return;
    }

    const numericValue = customer_savings === '' ? null : parseFloat(customer_savings);

    if (customer_savings !== '' && (Number.isNaN(numericValue) || numericValue < 0)) {
      toastr.error(t('taskDetailsModal.errors.invalidCostSavings'));
      return;
    }

    const updateParams = {
      customer_savings: numericValue
    };

    props.taskActions.updateTask(
      props.taskDetails.id,
      updateParams,
      t('taskDetailsModal.costSavingsUpdated', { defaultValue: 'Cost savings updated successfully' })
    ).then(
      () => {
        setIsEditingCostSavings(false);
        toastr.success(t('taskDetailsModal.costSavingsUpdatedSuccess'));
      },
      () => {
        // Error handling is done in the action
      }
    );
  };

  const getCostSavingsInputValue = () => {
    if (isEditingCostSavings) {
      return customer_savings || '';
    }
    if (customer_savings !== null && customer_savings !== undefined && customer_savings !== '') {
      return formatCurrency(
        parseFloat(customer_savings),
        getCurrencyFromState({ currentAccount: props.currentAccount }),
        { decimals: 2 }
      );
    }
    return '';
  };

  const handleCostSavingsDelete = () => {
    if (!props.taskDetails.id || !props.isEditMode) {
      return;
    }
    props.taskActions.updateTask(
      props.taskDetails.id,
      { customer_savings: null },
      t('taskDetailsModal.costSavingsRemoved', { defaultValue: 'Cost savings removed' })
    ).then(
      () => {
        setCustomerSavings('');
        setIsEditingCostSavings(false);
      },
      () => {}
    );
  };

  const getSelectedDefect = () => {
    // 0 is the N/A option's value; anything truthy here is sent to the API as defect_id.
    if (!props.isEditMode) return 0;
    if (!props.taskDetails.defect_id) return 0;
    const foundDefect = defectOptions.find(d => d.value === props.taskDetails.defect_id);
    return foundDefect ? foundDefect.value : null;
  };

  const getSelectedCondition = (condition) => {
    const conditionOptions = getMachineConditionOptions(props.assetConditionLevels, t);
    if (conditionOptions.find(opt => opt.value === condition)) return condition;
    return '';
  };

  const getSensorsList = (search_key = '') => {
    const filters = [
      { name: 'type',
        op: 'in',
        value: [
          'Vibration Mote (VM1)',
          'Vibration Mote (VM2)',
          'Vibration Mote (VM3)',
          'Vibration Mote (VM4P)',
          'Vibration Mote (GSP)',
          'Vibration Mote (VM4)',
          'Vibration Mote (GS)',
          'Transmitter',
          'Vibration Sensor',
          'Current Sensor',
          'Magnetometer Sensor',
          'Temperature Sensor',
          'Ultrasound Vector Sensor',
          'VSX'
        ]
      },
      { name: 'machine_id',
        op: 'in',
        value: [props.machineId || props.machine_id || props.taskDetails.machine_id]
      },
    ];
    props.getSensorsList(filters, search_key, 1, false).then(
      (res) => {
        setSensorOptions(res.items.map(item => ({
          id: item.id,
          serial_number: item.serial_number,
          model: item.model
        })));
      }
    );
  };

  const getSensorsDebounced = useCallback(_.debounce(key => getSensorsList(key), 300), []);

  const toggleDeviceOption = (sr_no) => {
    const clickedOption = sensorOptions.find(opt => opt.serial_number === sr_no);
    setSelectedDevices((prevList) => {
      let newList = [];
      if (prevList.find(opt => _.isEqual(clickedOption, opt))) {
        newList = prevList.filter(opt => !_.isEqual(clickedOption, opt));
      } else {
        newList = [...prevList, clickedOption];
      }
      if (props.isEditMode && !_.isEqual(newList, props.taskDetails.devices_list)) {
        onTaskUpdate('devices_list', newList);
      }
      return newList;
    });
  };

  const toggleFollower = (user_id, e = null) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setFollowers((prevList) => {
      let newList = [];
      if (prevList.includes(user_id)) {
        newList = prevList.filter(id => id !== user_id);
      } else {
        newList = [...prevList, user_id];
      }
      if (props.isEditMode && !_.isEqual(newList, props.taskDetails.followers)) {
        onTaskUpdate('followers', newList);
      }
      return newList;
    });
  };

  const toggleTrigger = (trigger) => {
    setSelectedTriggers((prevTriggers) => {
      const newTriggers = {
        ...prevTriggers,
        [trigger]: {
          ...prevTriggers[trigger],
          selected: !(prevTriggers[trigger] && prevTriggers[trigger].selected)
        }
      };
      if (props.isEditMode && !_.isEqual(newTriggers, props.taskDetails.triggers)) {
        const triggersToUpdate = _.cloneDeep(newTriggers);
        if (triggersToUpdate) {
          Object.keys(triggersToUpdate).forEach((key) => {
            delete triggersToUpdate[key].selected;
          });
        }
        onTaskUpdate('triggers_details', triggersToUpdate);
      }
      return newTriggers;
    });
  };

  const toggleEvent = (trigger, eventDetail) => {
    setSelectedTriggers((prevTriggers) => {
      const prevEvents = prevTriggers[trigger].resources_info || [];
      let newEvents = [];
      if (prevEvents.find(info => info.activity_id === eventDetail.activity_id)) {
        newEvents = prevEvents.filter(resource => resource.activity_id !== eventDetail.activity_id);
      } else {
        const resource = {
          activity_id: eventDetail.activity_id,
          description: eventDetail.description
        };
        if (eventDetail.tag_id && eventDetail.timestamp) {
          resource.urlInfo = {
            pathname: '/configs',
            searchParams: getChartSearchParams(eventDetail)
          };
        }
        newEvents = [...prevEvents, resource];
      }
      const newTriggers = {
        ...prevTriggers,
        [trigger]: {
          ...prevTriggers[trigger],
          resources_info: newEvents
        }
      };
      if (props.isEditMode && !_.isEqual(newTriggers, props.taskDetails.triggers)) {
        const triggersToUpdate = _.cloneDeep(newTriggers);
        if (triggersToUpdate) {
          Object.keys(triggersToUpdate).forEach((key) => {
            delete triggersToUpdate[key].selected;
          });
        }
        onTaskUpdate('triggers_details', triggersToUpdate);
      }
      return newTriggers;
    });
  };

  const getFollowersDisplayText = () => {
    let text = 0;
    if (followers.length) {
      text = followers.length;
      if (followers.includes(props.user.id)) {
        text = followers.length === 1
          ? t('taskDetailsModal.followersYou')
          : t('taskDetailsModal.followersYouPlus', { count: followers.length - 1 });
      }
    }
    return text;
  };

  const checkIfToRenderTriggers = () => {
    if (!props.isEditMode) return !_.isEmpty(props.taskDetails.allowed_triggers) || !_.isEmpty(props.allowedTriggers);
    return !_.isEmpty(selectedTriggers);
  };

  const nameChangeHandler = (e) => {
    const value = e.target.value;
    setName(value);
    if (value && value.length > 150) {
      setNameError(t('taskDetailsModal.nameMaxLengthError'));
    } else {
      setNameError('');
    }
  };

  const renderTrendIndicatorsInfo = () => {
    const info = props.taskDetails.trend_indicators_info;
    if (!info || _.isNaN(info.max_value)) return null;

    const maxValue = info.max_value;
    let color = colors.good;
    if (maxValue >= 50) color = colors.alarmCritical;
    else if (maxValue >= 25) color = colors.alarmWarning;
    return (
      <FlexContainer direction="column">
        <H3 color={color}>
          {`${_.round(Math.abs(maxValue), 2)}% `}
          {maxValue > 0 ? <ArrowUp /> : <ArrowDown />}
        </H3>
      </FlexContainer>
    );
  };

  const setInitialTriggers = () => {
    let allowed_triggers = [];
    if (!props.isEditMode) {
      allowed_triggers = _.isEmpty(props.taskDetails.allowed_triggers) ? props.allowedTriggers :
        props.taskDetails.allowed_triggers;
    } else {
      allowed_triggers = _.cloneDeep(props.taskDetails.triggers_details || []);
      Object.keys(allowed_triggers).forEach((key) => {
        allowed_triggers[key].selected = true;
      });
    }
    setAllowedTriggers(allowed_triggers);
    setSelectedTriggers(allowed_triggers);
  };

  const setRepairModalOpen = id => props.repairActions.openRepairModal(id);

  const setNoteModalOpen = id => props.noteActions.openNoteModal(id, props.taskDetails.id);

  const toggleRepairExpand = () => setRepairExpanded(prevState => !prevState);

  const toggleNoteExpand = () => setNoteExpanded(prevState => !prevState);

  const [name, setName] = useState('');
  const [template_name, setTemplateName] = useState('');
  const [state, setTaskState] = useState('open');
  const [description, setDescription] = useState('');
  const [recommended_action, setRecommendedAction] = useState('');
  const [previousDescription, setPreviousDescription] = useState('');
  const [previousRecommendedAction, setPreviousRecommendedAction] = useState('');
  const [customer_savings, setCustomerSavings] = useState('');
  const [isEditingCostSavings, setIsEditingCostSavings] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingRecommendedAction, setIsEditingRecommendedAction] = useState(false);
  const [showCloseSavingsPopup, setShowCloseSavingsPopup] = useState(false);
  const [pendingCloseState, setPendingCloseState] = useState(null);
  const [task_type, setTaskType] = useState('asset_maintenance');
  const [component_type, setComponentType] = useState('');
  const [user_role_assigned, setAssignedTo] = useState('');
  const [condition, setCondition] = useState('');
  const [devices_list, setSelectedDevices] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [selectedTriggers, setSelectedTriggers] = useState([]);

  const [defectOptions, setDefectOptions] = useState(getDefectOptions());
  const [defect, setDefect] = useState(getSelectedDefect());

  const [createDisabled, setCreateDisabled] = useState(true);
  const [sensorOptions, setSensorOptions] = useState([]);
  const [repairExpanded, setRepairExpanded] = useState(false);
  const [noteExpanded, setNoteExpanded] = useState(false);
  const [activeActivityTab, setActiveActivityTab] = useState('activity');
  const descriptionEditRef = useRef(null);
  const recommendedActionEditRef = useRef(null);
  const costSavedEditRef = useRef(null);
  const [deviceSearchKey, setSearchKey] = useState('');
  const [showSaveLoader, setShowSaveLoader] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState(false);
  const [nameError, setNameError] = useState('');
  const [template_Data, setTemplateData] = useState([]);
  const [componentsList, setComponentsList] = useState([]);
  const [allowedTriggers, setAllowedTriggers] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [leftPaneView, setLeftPaneView] = useState('details'); // 'details' | 'templates'
  const [templateSearch, setTemplateSearch] = useState('');
  const [activeDefectFilter, setActiveDefectFilter] = useState('all');
  const [showAllDefectFilters, setShowAllDefectFilters] = useState(false);

  const openTemplateSelectionView = () => {
    setLeftPaneView('templates');
  };

  const handleTemplateCardClick = (template) => {
    setSelectedTemplateId(template.id);
    onTemplateSelection(template.id);
    setLeftPaneView('details');
  };

  const availableDefects = template_Data && template_Data.length
    ? _.uniq(template_Data.map(t => t.suspected_defect).filter(Boolean))
    : [];
  const defaultDefectFilters = availableDefects.slice(0, DEFECT_FILTER_COLLAPSE_COUNT);
  const collapsedDefectFilters = (
    activeDefectFilter !== 'all'
    && !defaultDefectFilters.includes(activeDefectFilter)
  )
    ? [...defaultDefectFilters, activeDefectFilter]
    : defaultDefectFilters;
  const visibleDefectFilters = showAllDefectFilters
    ? availableDefects
    : collapsedDefectFilters;
  const hiddenDefectCount = Math.max(availableDefects.length - visibleDefectFilters.length, 0);

  const filteredTemplates = template_Data && template_Data.length
    ? template_Data.filter((t) => {
      const matchesSearch = !templateSearch ||
          (t.name && t.name.toLowerCase().includes(templateSearch.toLowerCase()));
      const matchesDefect = activeDefectFilter === 'all' ||
          t.suspected_defect === activeDefectFilter;
      return matchesSearch && matchesDefect;
    })
    : [];

  const costSavedDisplayValue = (() => {
    if (isEditingCostSavings) return customer_savings || '';
    if (customer_savings === null || customer_savings === undefined || customer_savings === '') return '';
    return formatCurrency(
      parseFloat(customer_savings),
      getCurrencyFromState({ currentAccount: props.currentAccount }),
      { decimals: 2 }
    );
  })();

  const getConditionColor = (conditionValue) => {
    if (!conditionValue) return '#9CA3AF';
    const val = String(conditionValue).toLowerCase();
    if (val === 'good') return '#22C55E'; // green
    if (val === 'warning') return '#EAB308'; // yellow
    if (val === 'critical') return '#EF4444'; // red
    return '#9CA3AF';
  };

  useEffect(() => {
    if (!props.taskMetadata) {
      props.getMachineBuilderMetadata();
    }

    props.getComponentOptions(Number(props.machineId || props.taskDetails.machine_id)).then(
      (res) => {
        const componentsList = getComponentList(res);
        setComponentsList(componentsList);
      }
    );

    if (!props.isEditMode) {
      const {
        getTemplatesList } = props.templateActions;
      getTemplatesList().then(
        (res) => {
          const items = (res && res.items) ? res.items : [];
          setTemplateData(items);
        }
      );
      setInitialTriggers();
    }

    if (props.match && props.match.params.task_id) {
      const task_id = parseInt(props.match.params.task_id, 10);
      props.taskActions.getTaskDetails(task_id, false);
    }

    if (_.isEmpty(props.users)) {
      props.getUsers();
    }
  }, []);

  useEffect(() => {
    if (props.machineId || props.taskDetails.machine_id) {
      getSensorsDebounced(deviceSearchKey);
    }
  }, [deviceSearchKey]);

  useEffect(() => {
    const { machineDetails, machineId, machineDetailsActions, failureModesActions } = props;
    if (
      (!machineDetails.object && (machineId || props.taskDetails.machine_id)) ||
      (machineDetails.object && (machineId !== props.taskDetails.machine_id))
    ) {
      machineDetailsActions.getMachineDetails(props.taskDetails.machine_id || machineId);
    }
    failureModesActions.getFailureModeList();

    if (machineId || props.taskDetails.machine_id) {
      getSensorsList();
    }
  }, [props.taskDetails.machine_id]);

  useEffect(() => {
    setDefectOptions(getDefectOptions());
  }, [props.failureModes, props.machineDetails.object]);

  useEffect(() => {
    if (
      (_.isEmpty(name) || _.isEmpty(task_type) || _.isEmpty(description)) ||
      (task_type === 'asset_maintenance' && _.isEmpty(condition))
    ) {
      setCreateDisabled(true);
    } else {
      setCreateDisabled(false);
    }
  }, [name, task_type, description, condition, component_type, template_name]);

  useEffect(() => {
    if (props.isEditMode) {
      setTemplateName(props.taskDetails.template_name);
      setName(props.taskDetails.name);
      setTaskState(props.taskDetails.state);
      setDescription(props.taskDetails.description);
      setRecommendedAction(props.taskDetails.recommended_action);
      // Handle customer_savings - only for asset maintenance tasks
      if (props.taskDetails.task_type === 'asset_maintenance') {
        const savingsValue = props.taskDetails.customer_savings;
        if (savingsValue !== null && savingsValue !== undefined) {
          setCustomerSavings(String(savingsValue));
        } else {
          setCustomerSavings('');
        }
      } else {
        setCustomerSavings('');
      }
      setIsEditingCostSavings(false);
      setIsEditingDescription(false);
      setIsEditingRecommendedAction(false);
      setTaskType(props.taskDetails.task_type);
      setAssignedTo(props.taskDetails.user_role_assigned);
      setSelectedDevices(props.taskDetails.devices_list);
      setFollowers(props.taskDetails.followers || []);
      setCondition(getSelectedCondition(props.taskDetails.condition));
      setDefectOptions(getDefectOptions());
      setComponentType(props.taskDetails.component_type);
      setInitialTriggers();
    }
  }, [props.taskDetails]);

  useEffect(() => {
    setDefect(getSelectedDefect());
  }, [defectOptions]);

  useEffect(() => {
    // Reset edit states when task details change
    setIsEditingDescription(false);
    setIsEditingRecommendedAction(false);
    setActiveActivityTab('activity');
  }, [props.taskDetails.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const closeBtnEl = e.target.closest && e.target.closest('.modal-close');
      if (closeBtnEl) return;

      if (isEditingDescription && descriptionEditRef.current && !descriptionEditRef.current.contains(e.target)) {
        setDescription(props.taskDetails.description || '');
        setIsEditingDescription(false);
      }
      if (isEditingRecommendedAction && recommendedActionEditRef.current && !recommendedActionEditRef.current.contains(e.target)) {
        setRecommendedAction(props.taskDetails.recommended_action || '');
        setIsEditingRecommendedAction(false);
      }
      if (isEditingCostSavings && costSavedEditRef.current && !costSavedEditRef.current.contains(e.target)) {
        setCustomerSavings(props.taskDetails.customer_savings != null ? String(props.taskDetails.customer_savings) : '');
        setIsEditingCostSavings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingDescription, isEditingRecommendedAction, isEditingCostSavings, props.taskDetails.description, props.taskDetails.recommended_action, props.taskDetails.customer_savings]);

  useEffect(() => {
    // checks if upload has been completed and closes the modal
    const completed = props.upload && props.upload.index === props.files.length - 1 && props.upload.progress >= 100;
    if (!props.isEditMode && completed && !_.isEmpty(props.files)) {
      setShowSaveLoader(false);
      props.closeModal();
    }
  }, [props.upload]);

  const trendIndicatorsInfo = props.taskDetails && props.taskDetails.trend_indicators_info;
  const machineName = props.isEditMode ? props.taskDetails.machine_name : props.machineName;
  const eventsDetails = _.isEmpty(props.taskDetails.eventsDetails) ? props.eventsDetails :
    props.taskDetails.eventsDetails;

  const handleClose = () => {
    const state = window.history.state;
    if (state && state.prevLocation) {
      window.history.replaceState(null, '', state.prevLocation.pathname + (state.prevLocation.search || ''));
    }
    props.closeModal();
  };

  const menuItems = [{ text: t('taskDetailsModal.saveAsTemplate'), onClick: onTaskTemplateCreate }];

  return (
    <Modal
      title={() => <TaskTitleWithId isEdit={props.isEditMode} taskId={props.taskDetails.id} machineName={machineName} />}
      padding="0"
      outerPadding="0"
      headerPadding="1rem"
      headerRadius="12px"
      width="72rem"
      maxWidth
      menuItems={menuItems}
      close={handleClose}
      showHeaderBorder
    >
      <FlexContainer
        height="50rem"
        style={{ maxHeight: 'calc(100vh - var(--title-bar-offset, 0px) - 150px)' }}
        position="relative"
      >
        <ListContainer
          width="75%"
          padding="20px 24px"
        >
          {!props.isEditMode && leftPaneView === 'templates' ? (
            <RBAC
              resource={mapComponentToResource.TaskTemplates}
              operation={operations.Read}
              yes={(
                <>
                  <FlexContainer justifyContent="space-between" alignItems="center" style={{ marginBottom: '12px' }}>
                    <SectionHeader style={{ marginBottom: 0 }}>{t('taskDetailsModal.selectTemplateHeading')}</SectionHeader>
                    <BackToDetailsButton secondary onClick={() => setLeftPaneView('details')}>
                      {t('taskDetailsModal.backToDetails')}
                    </BackToDetailsButton>
                  </FlexContainer>
                  <SectionBlock style={{ marginBottom: 16 }}>
                    <InputField
                      type="text"
                      placeholder={t('taskDetailsModal.searchTemplatesPlaceholder')}
                      value={templateSearch}
                      onChange={e => setTemplateSearch(e.target.value)}
                      label=""
                      marginBottom="0"
                    />
                  </SectionBlock>
                  {availableDefects.length > 0 && (
                    <SectionBlock style={{ marginBottom: 16 }}>
                      <TemplateFilterRow>
                        <TemplateFilterPill
                          type="button"
                          active={activeDefectFilter === 'all'}
                          onClick={() => setActiveDefectFilter('all')}
                        >
                          {t('taskDetailsModal.allTemplates')}
                        </TemplateFilterPill>
                        {visibleDefectFilters.map(defect => (
                          <TemplateFilterPill
                            key={defect}
                            type="button"
                            active={activeDefectFilter === defect}
                            onClick={() => setActiveDefectFilter(defect)}
                          >
                            {defect}
                          </TemplateFilterPill>
                        ))}
                      </TemplateFilterRow>
                      {((!showAllDefectFilters && hiddenDefectCount > 0)
                        || (showAllDefectFilters && availableDefects.length > DEFECT_FILTER_COLLAPSE_COUNT)) && (
                        <TemplateFilterExpandRow>
                          {!showAllDefectFilters && hiddenDefectCount > 0 && (
                            <TemplateFilterToggle
                              type="button"
                              onClick={() => setShowAllDefectFilters(true)}
                            >
                              {t('taskDetailsModal.showMoreDefectFilters', { count: hiddenDefectCount })}
                            </TemplateFilterToggle>
                          )}
                          {showAllDefectFilters && availableDefects.length > DEFECT_FILTER_COLLAPSE_COUNT && (
                            <TemplateFilterToggle
                              type="button"
                              onClick={() => setShowAllDefectFilters(false)}
                            >
                              {t('taskDetailsModal.showLessDefectFilters')}
                            </TemplateFilterToggle>
                          )}
                        </TemplateFilterExpandRow>
                      )}
                    </SectionBlock>
                  )}
                  <TemplateCardsRow>
                    {filteredTemplates.length ? (
                      filteredTemplates.map(template => (
                        <TemplateCard
                          key={template.id}
                          selected={selectedTemplateId === template.id}
                          onClick={() => handleTemplateCardClick(template)}
                        >
                          <TemplateCardTitle>{template.name}</TemplateCardTitle>
                          {template.description && (
                            <TemplateCardDescription>{template.description}</TemplateCardDescription>
                          )}
                          <TemplateCardSteps>
                            {template.condition && (
                              <span style={{ color: getConditionColor(template.condition) }}>
                                {t(`machineCondition.${String(template.condition).toLowerCase()}`, { defaultValue: humanize(template.condition) })}
                              </span>
                            )}
                          </TemplateCardSteps>
                        </TemplateCard>
                      ))
                    ) : (
                      <MsgContainer style={{ width: '100%', margin: '8px 0 0 0' }}>
                        {t('common.noResultsFound', { defaultValue: 'No results found' })}
                      </MsgContainer>
                    )}
                  </TemplateCardsRow>
                </>
              )}
              no={null}
            />
          ) : (
            <>
              {!props.isEditMode && (
                <RBAC
                  resource={mapComponentToResource.TaskTemplates}
                  operation={operations.Read}
                  yes={(
                    <SelectTemplateBanner onClick={openTemplateSelectionView}>
                      <SelectTemplateTitle>{t('taskDetailsModal.selectTemplateBannerTitle')}</SelectTemplateTitle>
                      <SelectTemplateSubtitle>{t('taskDetailsModal.selectTemplateBannerSubtitle')}</SelectTemplateSubtitle>
                    </SelectTemplateBanner>
                  )}
                  no={null}
                />
              )}

              {!props.isEditMode && <TaskTitleUnderline />}

              <SectionBlock style={{ marginTop: 8, marginBottom: 4 }}>
                {!props.isEditMode && <SectionHeader>{t('taskDetailsModal.taskNameLabel')}</SectionHeader>}
                <TaskTitleWrapper>
                  <FlexContainer>
                    <InputEditOnDemand
                      inputType="title"
                      type="text"
                      placeholder={t('taskDetailsModal.enterTaskNamePlaceholder')}
                      id="task-name"
                      value={name}
                      nameError={nameError}
                      onChange={nameChangeHandler}
                      onSave={value => onTaskUpdate('name', value)}
                      isSaveDisabled={name === props.taskDetails.name || !_.isEmpty(nameError)}
                      creationMode={!props.isEditMode}
                      width="100%"
                    />
                  </FlexContainer>

                  {props.isEditMode && trendIndicatorsInfo && trendIndicatorsInfo.max_value && (
                    <FlexContainer direction="column" padding="0 0 1.3em 0.5em" justifyContent="flex-end">
                      <Popup trigger={renderTrendIndicatorsInfo()} flowing hoverable position="right top">
                        <Title>{t('taskDetailsModal.trendComponent')}</Title>
                        <Description>{trendIndicatorsInfo.component}</Description>
                        <Title>{t('taskDetailsModal.trendLocation')}</Title>
                        <Description>{trendIndicatorsInfo.location}</Description>
                        <Title>{t('taskDetailsModal.trendDirection')}</Title>
                        <Description>{trendIndicatorsInfo.direction}</Description>
                        <Title>{t('taskDetailsModal.trendFeature')}</Title>
                        <Description>{trendIndicatorsInfo.feature_name}</Description>
                      </Popup>
                    </FlexContainer>
                  )}
                </TaskTitleWrapper>
              </SectionBlock>

              <SectionBlock style={{ marginTop: props.isEditMode ? -4 : -8, marginBottom: props.isEditMode ? 4 : undefined }}>
                <div ref={descriptionEditRef}>
                  <SectionHeader>{t('taskDetailsModal.descriptionLabel')}</SectionHeader>
                  <DescriptionBoxWrapper
                    isEditing={isEditingDescription}
                    onClick={() => {
                      if (!isEditingDescription) {
                        setPreviousDescription(description);
                        setIsEditingDescription(true);
                      }
                    }}
                  >
                    <InputField
                      type="textarea"
                      placeholder={t('taskDetailsModal.descriptionPlaceholder')}
                      height="100px"
                      label=""
                      value={description}
                      readOnly={!isEditingDescription}
                      onChange={e => setDescription(e.target.value)}
                      marginBottom="0"
                    />
                  </DescriptionBoxWrapper>
                  {isEditingDescription && (
                    <Footer
                      onCancel={() => {
                        setDescription(previousDescription);
                        setIsEditingDescription(false);
                      }}
                      onSave={() => {
                        if (props.isEditMode && props.taskDetails.id) {
                          onTaskUpdate('description', description);
                        }
                        setIsEditingDescription(false);
                      }}
                      saveDisabled={props.isEditMode && description === props.taskDetails.description}
                    />
                  )}
                </div>
              </SectionBlock>

              <SectionBlock>
                <div ref={recommendedActionEditRef}>
                  <SectionHeader>{t('taskDetailsModal.recommendedActionLabel')}</SectionHeader>
                  <RecommendedActionBoxWrapper
                    isEditing={isEditingRecommendedAction}
                    onClick={() => {
                      if (!isEditingRecommendedAction) {
                        setPreviousRecommendedAction(recommended_action);
                        setIsEditingRecommendedAction(true);
                      }
                    }}
                  >
                    <InputField
                      type="textarea"
                      placeholder={t('taskDetailsModal.recommendedActionPlaceholder')}
                      height="100px"
                      label=""
                      value={recommended_action}
                      readOnly={!isEditingRecommendedAction}
                      onChange={e => setRecommendedAction(e.target.value)}
                      marginBottom="0"
                    />
                  </RecommendedActionBoxWrapper>
                  {isEditingRecommendedAction && (
                    <Footer
                      onCancel={() => {
                        setRecommendedAction(previousRecommendedAction);
                        setIsEditingRecommendedAction(false);
                      }}
                      onSave={() => {
                        if (props.isEditMode && props.taskDetails.id) {
                          onTaskUpdate('recommended_action', recommended_action);
                        }
                        setIsEditingRecommendedAction(false);
                      }}
                      saveDisabled={props.isEditMode && recommended_action === props.taskDetails.recommended_action}
                    />
                  )}
                </div>
              </SectionBlock>

              {props.isEditMode && state === 'closed' && task_type === 'asset_maintenance' && (
                <SectionBlock>
                  <div ref={costSavedEditRef}>
                    <CostSavedOuterWrapper
                      isEditing={isEditingCostSavings}
                      onClick={() => {
                        if (!isEditingCostSavings) setIsEditingCostSavings(true);
                      }}
                    >
                      <SectionHeader>{t('taskDetailsModal.costSavedLabel')}</SectionHeader>
                      <CostSavedInputWrapper
                        hasFooter={isEditingCostSavings}
                        onClick={() => {
                          if (!isEditingCostSavings) setIsEditingCostSavings(true);
                        }}
                      >
                        <InputField
                          type="text"
                          label=""
                          placeholder={isEditingCostSavings ? t('taskDetailsModal.enterCostSavedPlaceholder') : ''}
                          value={getCostSavingsInputValue()}
                          readOnly={!isEditingCostSavings}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) setCustomerSavings(value);
                          }}
                          marginBottom="0"
                        />
                      </CostSavedInputWrapper>
                      <CostSavedHelper>{t('taskDetailsModal.costSavedHelperText')}</CostSavedHelper>
                    </CostSavedOuterWrapper>
                    {isEditingCostSavings && (
                      <Footer
                        showDelete
                        onDelete={handleCostSavingsDelete}
                        deleteDisabled={false}
                        onCancel={() => {
                          setCustomerSavings(
                            props.taskDetails.customer_savings != null ? String(props.taskDetails.customer_savings) : ''
                          );
                          setIsEditingCostSavings(false);
                        }}
                        onSave={handleCostSavingsUpdate}
                        saveDisabled={
                          customer_savings === (props.taskDetails.customer_savings != null ? String(props.taskDetails.customer_savings) : '') ||
                          (customer_savings !== '' && (Number.isNaN(Number.parseFloat(customer_savings)) || Number.parseFloat(customer_savings) < 0))
                        }
                      />
                    )}
                  </div>
                </SectionBlock>
              )}

              <Attachments
                originType="task"
                originId={props.taskDetails.id}
                attachments={props.taskDetails.attachments}
                allowUpload={props.isEditMode}
                showExistingAtts={props.isEditMode}
                showSelectedFiles={!props.isEditMode}
                setAttachmentsError={setAttachmentsError}
              />
              {checkIfToRenderTriggers() && (
                <TaskTriggers
                  allowedTriggers={allowedTriggers}
                  selectedTriggers={selectedTriggers}
                  toggleTrigger={toggleTrigger}
                  toggleEvent={toggleEvent}
                  isEditMode={props.isEditMode}
                  eventsDetails={eventsDetails}
                />
              )}
              {props.isEditMode && (
                <>
                  <CollapsibleBox
                    label={<SectionHeader as="span" style={{ marginBottom: 0 }}>{t('taskDetailsModal.newRepair')}</SectionHeader>}
                    expanded={repairExpanded}
                    toggleExpand={toggleRepairExpand}
                  >
                    <RepairDetails
                      showCancel={false}
                      taskId={props.taskDetails.id}
                      machine_id={props.machineId || props.taskDetails.machine_id}
                      toggleExpand={toggleRepairExpand}
                    />
                  </CollapsibleBox>

                  <CollapsibleBox
                    label={<SectionHeader as="span" style={{ marginBottom: 0 }}>{t('taskDetailsModal.newNote')}</SectionHeader>}
                    expanded={noteExpanded}
                    toggleExpand={toggleNoteExpand}
                  >
                    <NoteDetails
                      showCancel={false}
                      taskId={props.taskDetails.id}
                      machine_id={props.machineId || props.taskDetails.machine_id}
                      toggleExpand={toggleNoteExpand}
                    />
                  </CollapsibleBox>

                  <ActivityTabRow>
                    <ActivityTab
                      active={activeActivityTab === 'activity'}
                      onClick={() => setActiveActivityTab('activity')}
                    >
                      {t('taskDetailsModal.tabs.taskActivity')}
                    </ActivityTab>
                    <ActivityTab
                      active={activeActivityTab === 'notes'}
                      onClick={() => setActiveActivityTab('notes')}
                    >
                      {t('taskDetailsModal.tabs.notes')}
                    </ActivityTab>
                    <ActivityTab
                      active={activeActivityTab === 'repairs'}
                      onClick={() => setActiveActivityTab('repairs')}
                    >
                      {t('taskDetailsModal.tabs.repairs')}
                    </ActivityTab>
                  </ActivityTabRow>
                  {activeActivityTab === 'activity' && (
                    <TaskHistory
                      loading={props.taskDetails.historyLoading}
                      items={props.taskDetails.history}
                      setRepairModalOpen={setRepairModalOpen}
                      setNoteModalOpen={setNoteModalOpen}
                      taskId={props.taskDetails.id}
                      repairs={props.taskDetails.repairs}
                      notes={props.taskDetails.notes}
                      currency={getCurrencyFromState({ currentAccount: props.currentAccount })}
                    />
                  )}
                  {activeActivityTab === 'repairs' && (
                    <RepairsTimeline
                      repairs={props.taskDetails.repairs}
                      taskId={props.taskDetails.id}
                      setRepairModalOpen={setRepairModalOpen}
                    />
                  )}
                  {activeActivityTab === 'notes' && (
                    <NotesTimeline
                      notes={props.taskDetails.notes}
                      taskId={props.taskDetails.id}
                      setNoteModalOpen={setNoteModalOpen}
                    />
                  )}
                </>
              )}
            </>
          )}
        </ListContainer>
        <ListContainer
          width="30%"
          borderLeft
          borderRadius="0 0 5px 0"
        >
          <RightPaneFields>
          <DropdownSelect
            onClickItem={(_text, value, _parText) => onDropdownChange(value, 'state')}
            placeholder={t('taskDetailsModal.selectState')}
            label={t('taskDetailsModal.stateLabel')}
            disabled={!props.isEditMode}
            data={props.taskMetadata ? getTaskStateOptions(props.taskMetadata.states) : []}
            value={getTaskStateLabel(state)}
            heightLimit
          />
          <DropdownSelect
            onClickItem={(_text, value, _parText) => onDropdownChange(value, 'user_role_assigned')}
            placeholder={t('taskDetailsModal.assignPlaceholder')}
            label={t('taskDetailsModal.assignedToLabel')}
            data={getRoleOptions()}
            value={getRoleLabel(user_role_assigned)}
            heightLimit
          />
          <DropdownSelect
            label={t('taskDetailsModal.componentTypeLabel')}
            placeholder={t('common.select')}
            value={getComponentTypeLabel(component_type)}
            data={componentsList}
            onClickItem={(_text, value, _parText) => onDropdownChange(value, 'component_type')}
            heightLimit
            clearable
          />
          <DropdownSelect
            onClickItem={(_text, value, _parText) => onDropdownChange(value, 'task_type')}
            placeholder={t('common.select')}
            label={t('taskDetailsModal.taskTypeLabel')}
            data={getTaskTypeOptions()}
            value={getTaskTypeLabel(task_type)}
            heightLimit
          />
          <DropdownSelect
            onClickItem={(_text, value, _parText) => onDropdownChange(value, 'condition')}
            placeholder={t('taskDetailsModal.selectCondition')}
            label={t('taskDetailsModal.conditionLabel')}
            data={getMachineConditionOptions(props.assetConditionLevels, t)}
            value={condition
              ? t(`machineCondition.${String(condition).toLowerCase()}`, { defaultValue: humanize(condition) })
              : ''}
            heightLimit
          />

          {task_type === 'asset_maintenance' && (
            <>
              <SectionBlock>
                <SectionHeader>{t('taskDetailsModal.defectLabel')}</SectionHeader>
                <SelectFieldWrapper>
                  <InputField
                    label=""
                    type="select"
                    options={defectOptions}
                    placeholder={t('taskDetailsModal.selectDefect')}
                    value={defect}
                    onChange={(_e, { value }) => onDropdownChange(value, 'defect_id')}
                    fontSettings={fonts.tableRow}
                    optionFontSettings={fonts.selectOption}
                    search
                  />
                </SelectFieldWrapper>
              </SectionBlock>
            </>
          )}
          {task_type === 'instrumentation' && (
            <SectionBlock>
              <SectionHeader>{t('taskDetailsModal.devicesLabel')}</SectionHeader>
              <MultiSelectDropdown
                className="ed-multi-select-dropdown"
                key="instrumentation-devices"
                fontSize="14px"
                optionsAvailable={sensorOptions.map(
                  op => ({ text: op.serial_number, value: op.serial_number })
                )}
                selectedOptions={devices_list.map(opt => opt.serial_number)}
                disableSelectAll
                toggleOption={toggleDeviceOption}
                id="devices-select"
                label=""
                placeholder={t('taskDetailsModal.selectDevices')}
                setSearchField={setSearchKey}
                backendSearch
                showEmptyMsg
              />
            </SectionBlock>
          )}
          {props.isEditMode && (
            <>
              <SectionHeader style={{ marginTop: '18px' }}>{t('taskDetailsModal.followersLabel')}</SectionHeader>
              <MultiSelectDropdown
                className="ed-multi-select-dropdown followers-select-dropdown"
                key="task-followers"
                fontSize="14px"
                optionsAvailable={props.users.map(
                  user => ({ text: `${user.first_name} ${user.last_name}`, value: user.id })
                )}
                selectedOptions={followers}
                disableSelectAll
                toggleOption={toggleFollower}
                id="followers-list"
                customSelectionView={() => (
                  <FollowersSelectionView
                    text={getFollowersDisplayText()}
                    showFollowIcon={!followers.includes(props.user.id)}
                    onFollowClick={e => toggleFollower(props.user.id, e)}
                    followTaskTitle={t('taskDetailsModal.followThisTask')}
                  />
                )}
                showEmptyMsg
              />
            </>
          )}
          </RightPaneFields>
        </ListContainer>
        {(props.taskDetails.loading || showSaveLoader) && <Loading position="absolute" height="100%" backgroundColor="#e4e4e447" />}
      </FlexContainer>
      {!props.isEditMode && (
        <Footer
          showBorderTop
          onCancel={props.closeModal}
          onCreateTaskTemplate={onTaskTemplateCreate}
          showCreateTaskTemplate={false}
          onSave={onTaskSave}
          saveDisabled={createDisabled || showSaveLoader || attachmentsError || !_.isEmpty(nameError)}
        />
      )}
      {showCloseSavingsPopup && (
        <Modal
          open={showCloseSavingsPopup}
          close={handleCloseSavingsCancel}
          title={t('taskDetailsModal.costSavingsModalTitle')}
          width="400px"
        >
          <FlexContainer direction="column" padding="20px">
            <SectionHeader>{t('taskDetailsModal.costSavedLabel')}</SectionHeader>
            <CloseTaskInputWrapper>
              <InputField
                type="number"
                placeholder={t('taskDetailsModal.enterCostSavedPlaceholder')}
                value={customer_savings}
                onChange={e => setCustomerSavings(e.target.value)}
              />
            </CloseTaskInputWrapper>
            <FlexContainer justifyContent="flex-end" style={{ marginTop: '20px', gap: '10px' }}>
              <Button secondary onClick={handleCloseSavingsCancel}>
                {t('common.cancel')}
              </Button>
              <Button primary onClick={handleCloseSavingsConfirm} style={{ backgroundColor: colors.green, color: '#fff' }}>
                {t('taskDetailsModal.closeTask')}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </Modal>
      )}
    </Modal>
  );
}

const mapStateToProps = state => ({
  currentAccount: state.currentAccount,
  taskMetadata: state.machines.machineBuilder.metadata.task_metadata,
  assetConditionLevels: state.currentAccount.preferences.machine_condition_levels,
  machineDetails: state.machineDetails.details,
  failureModes: state.assetFailureMode.failureModes,
  machineName: state.machineDetails.details.object && state.machineDetails.details.object.name,
  machineId: state.machineDetails.details.object && state.machineDetails.details.object.id,
  taskDetails: state.taskModal.openTaskDetails,
  taskTemplates: state.taskTemplates,
  users: state.settings.eventDefinitions.static_data.users,
  user: state.user.user,
  files: state.attachments.files && state.attachments.files.task,
  upload: state.attachments.upload && state.attachments.upload.task,
  hierarchy: state.assetHierarchyReducer.assetInfo.hierarchy
});

const mapDispatchToProps = dispatch => ({
  getMachineBuilderMetadata: bindActionCreators(getMachineBuilderMetadata, dispatch),
  getSensorsList: bindActionCreators(getSensorsList, dispatch),
  getComponentOptions: bindActionCreators(getComponentOptions, dispatch),
  getUsers: bindActionCreators(getUsers, dispatch),
  taskActions: bindActionCreators(taskActions, dispatch),
  templateActions: bindActionCreators(templateActions, dispatch),
  repairActions: bindActionCreators(repairActions, dispatch),
  noteActions: bindActionCreators(noteActions, dispatch),
  machineDetailsActions: bindActionCreators(machineDetailsActions, dispatch),
  failureModesActions: bindActionCreators(failureModesActions, dispatch),
});

TaskDetailsModal.propTypes = {
  isEditMode: PropTypes.bool,
  closeModal: PropTypes.func,
  eventsDetails: PropTypes.object,
  allowedTriggers: PropTypes.object
};

TaskDetailsModal.defaultProps = {
  isEditMode: false,
  closeModal: () => {},
  eventsDetails: [],
  allowedTriggers: {}
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TaskDetailsModal));
